import { useFormik, Form, FormikProvider } from "formik";
import { Button, Input } from "styled";
import InputField from "components/formik/InputField";
import Model from "components/Modal";
import { createDocument, getAllData } from "apis/customApiHandler";
import { useQuery, useQueryClient } from "react-query";
import toast from "react-hot-toast";
import { useCallback, useState } from "react";
import ReactSelect from "react-select";
import getFormattedDate from "utils/helpers";

const AddContractMaster = ({ handleClick }) => {
    const queryClient = useQueryClient();
    const [selectedFleets, setSelectedFleets] = useState([]);
    const [selectedHubs, setSelectedHubs] = useState([]);
    const [fleetMap, setFleetMap] = useState({});
    const [effectiveDate, setEffectiveDate] = useState("");
    const [renewalDate, setRenewalDate] = useState("");

    const formik = useFormik({
        initialValues: {
            name: "",
        },
        onSubmit: async (values) => {
            const data = await createDocument("contract-type", values);
            if (data) {
                toast.success("Successfully saved");
                queryClient.invalidateQueries("contract-types");
            }
            handleClick();
        },

        validate: (values) => {
            let errors = {};
            if (!values.name) errors.name = "Required";
            return errors;
        },
    });

    const { data: allFleets } = useQuery([`/customer-fleet`], () =>
        getAllData("/customer-fleet", {
            limit: 3000,
        })
    );
    const { data: allHubs } = useQuery([`/customer-hub`], () =>
        getAllData("/customer-hub", {
            limit: 3000,
            ignoreAssignedHubs: true,
        })
    );

    const getFleetOptions = useCallback(() => {
        if (allFleets && allFleets.data && allFleets.data.length) {
            return [
                {
                    label: "All",
                    value: "all",
                },
                {
                    label: "Clear",
                    value: "clear",
                },
                ...allFleets?.data?.map((fleet) => {
                    return {
                        value: fleet._id,
                        label: fleet.fleetName,
                    };
                }),
            ];
        }
    }, [allFleets]);
    const getHubOptions = useCallback(() => {
        if (allHubs && allHubs.data && allHubs.data.length) {
            return [
                selectedFleets?.length && {
                    label: "All",
                    value: "all",
                },
                {
                    label: "Clear",
                    value: "clear",
                },
                ...(selectedFleets?.includes("all") ?
                    allHubs?.data?.length &&
                    allHubs?.data
                        ?.map((hub) => {
                            return {
                                value: hub._id,
                                label: hub.hubName,
                            };
                        }) :
                    allHubs?.data?.length &&
                    allHubs?.data
                        ?.filter((hub) => {
                            const selectedFleetsVal = allFleets?.data?.filter((fleet) =>
                                selectedFleets?.includes(fleet._id)
                            );

                            return selectedFleetsVal?.some(
                                (fleet) => fleet.fleetCode === hub.fleetCode
                            );
                        })
                        ?.map((hub) => {
                            return {
                                value: hub._id,
                                label: hub.hubName,
                            };
                        })),
            ];
        }
    }, [allFleets, allHubs, selectedFleets]);

    return (
        <Model
            handleClick={handleClick}
            title={"Add Detail"}
            className="min-w-4xl min-h-screen"
        >
            <FormikProvider value={formik}>
                <Form className="px-5 text-gray-700 font-medium text-base grid  lg:gap-x-14 gap-y-5 grid-flow-row grid-cols-1 lg:grid-cols-2 w-full">
                    <label className="w-full py-3">
                        Select Fleets
                        <ReactSelect
                            styles={{
                                control: (base, state) => ({
                                    ...base,
                                    borderColor: "transparent",
                                    minWidth: "100%",
                                    "&:hover": {
                                        borderColor: "rgb(34 197 94)",
                                    },
                                }),
                            }}
                            className="shadow-md rounded-md py-1 mb-2"
                            options={getFleetOptions()}
                            value={
                                selectedFleets &&
                                selectedFleets?.map((fleet) => {
                                    return {
                                        value: fleet,
                                        label: getFleetOptions()?.find((item) => item?.value === fleet)
                                            ?.label,
                                    };
                                })
                            }
                            onChange={(value) => {
                                let allValues = value?.map((item) => item.value);
                                if (allValues?.includes("clear")) {
                                    // console.log(allValues)
                                    setSelectedFleets([]);
                                    setSelectedHubs([]);
                                    return;
                                }
                                if (!selectedFleets?.includes("all") && allValues.includes("all")) {
                                    setSelectedFleets(["all"]);
                                    return;
                                }

                                if (selectedFleets.includes("all")) {
                                    // console.log("first");
                                    // console.log(getFleetOptions())
                                    value = value.filter(item => item.value !== "all")
                                }

                                let selectedFleetsVal = allFleets?.data?.filter((fleet) =>
                                    value?.map((item) => item.value)?.includes(fleet._id)
                                );

                                let selectedHubsVal = allHubs?.data?.filter((hub) =>
                                    selectedHubs?.map((item) => item)?.includes(hub._id)
                                );

                                setSelectedHubs(
                                    selectedHubsVal
                                        ?.filter((hub) =>
                                            selectedFleetsVal?.find(
                                                (fleet) => fleet.fleetCode === hub.fleetCode
                                            )
                                        )
                                        .map((hub) => hub._id)
                                );

                                // console.log({allHubs, selectedHubs})

                                setSelectedFleets(value.map((fleet) => fleet.value));
                            }
                            }
                            name="fleets"
                            isMulti={true}
                        />
                    </label>
                    <label className="w-full py-3">
                        Select Hubs
                        <ReactSelect
                            styles={{
                                control: (base, state) => ({
                                    ...base,
                                    borderColor: "transparent",
                                    minWidth: "100%",
                                    "&:hover": {
                                        borderColor: "rgb(34 197 94)",
                                    },
                                }),
                            }}
                            className="shadow-md rounded-md py-1 mb-2"
                            options={getHubOptions()}
                            value={
                                selectedHubs &&
                                selectedHubs?.map((hub) => {
                                    // console.log({hub})
                                    return {
                                        value: hub,
                                        label: getHubOptions()?.find((item) => item.value === hub)
                                            ?.label,
                                    };
                                })
                            }
                            onChange={(value) => {
                                const allValues = value?.map((item) => item.value);
                                // console.log({allValues})
                                if (allValues.includes("clear")) {
                                    setSelectedHubs([])
                                    return;
                                }
                                if (!selectedHubs?.includes("all") && allValues.includes("all")) {
                                    let selectedFleetCodes = allFleets?.data?.filter((fleet) =>
                                        selectedFleets?.includes(fleet._id)
                                    )?.map(fleet => fleet.fleetCode);

                                    const selectedHubsData = allHubs?.data?.filter(hub => selectedFleetCodes?.includes(hub.fleetCode))

                                    const mp = fleetMap;
                                    selectedHubsData.map(hub => {
                                        if (selectedFleetCodes?.includes(hub.fleetCode)) {
                                            if (mp[hub.fleetCode] === undefined) mp[hub.fleetCode] = [];
                                            mp[hub.fleetCode] = [...mp[hub.fleetCode], hub._id]
                                        }
                                    })

                                    setFleetMap(mp);
                                    setSelectedHubs(["all"])
                                }
                                else {
                                    // console.log({value})
                                    value = value.filter(value => value.value !== "all")
                                    // console.log({value})
                                    let selectedFleetCodes = allFleets?.data?.filter((fleet) =>
                                        selectedFleets?.includes(fleet._id)
                                    )?.map(fleet => fleet.fleetCode);
                                    // console.log({allValues})

                                    const selectedHubsData = allHubs?.data?.filter(hub => allValues?.includes(hub._id))
                                    const mp = {};
                                    selectedHubsData.map(hub => {
                                        mp[hub.fleetCode] = [];
                                    })

                                    selectedHubsData.map(hub => {
                                        if (selectedFleetCodes?.includes(hub.fleetCode)) {
                                            if (mp[hub.fleetCode] === undefined) mp[hub.fleetCode] = [];
                                            mp[hub.fleetCode] = [...mp[hub.fleetCode], hub._id]
                                        }
                                    })

                                    setFleetMap(mp);
                                    setSelectedHubs(value.map((hub) => hub.value));
                                }
                            }}
                            name="hubs"
                            isMulti={true}
                        />
                    </label>
                    <label>
                        Effective Date{" "}
                        <Input
                            type="date"
                            max={getFormattedDate()}
                            name="effectiveDate"
                            value={effectiveDate}
                            onChange={(e) => setEffectiveDate(e.target.value)}
                        />
                    </label>
                    <label>
                        Renewal Date{" "}
                        <Input
                            type="date"
                            min={getFormattedDate(new Date(effectiveDate).getTime() + 86400000)}
                            name="renewalDate"
                            value={renewalDate}
                            onChange={(e) => setRenewalDate(e.target.value)}
                            disabled={!effectiveDate}
                        />
                    </label>

                </Form>
                <div className="px-5 flex justify-end items-center gap-4 pb-8 flex-wrap sm:w-full">
                    <Button bgSecondary onClick={handleClick}>
                        Cancel
                    </Button>
                    <Button type="button" bgPrimary onClick={formik.handleSubmit}>
                        Save Changes
                    </Button>
                </div>
            </FormikProvider>
        </Model>
    )
}

export default AddContractMaster
